import { Badge, cn, Popover, PopoverContent, PopoverTrigger, Skeleton } from '@atlas/ui-primitives'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { EmptyState } from '../empty-state/empty-state'
import type { FilterAxis } from './types'

/** The closed trigger's box, shared with the skeleton so the two stand exactly the same height. */
const TRIGGER_CLASS =
	'flex w-full items-center justify-between gap-2 rounded-ds-md border border-ds-border bg-ds-surface-elevated px-3 py-2 text-left text-ds-sm leading-5'

export interface AxisMultiSelectProps {
	/** The axis whose values are pickable. */
	axis: FilterAxis
	/** Value codes currently selected on this axis. */
	selectedCodes: string[]
	/** Toggle a value on the axis (select if absent, deselect if present). */
	onToggle: (valueCode: string) => void
	/** Optional remove affordance for a dynamically-added axis group — additive; existing consumers omit it. */
	onRemove?: () => void
}

/** Summarise the current selection for the trigger label. */
function summary(axis: FilterAxis, selectedCodes: string[]): string {
	// Not a Skeleton, and deliberately so: this is the trigger's text, and saying
	// 'Any' while the axis is still loading would present an unloaded axis as an
	// empty one. filter-rail.test.tsx pins that distinction.
	if (selectedCodes.length === 0) return axis.isLoading ? 'Loading…' : 'Any'
	return axis.options
		.filter((o) => selectedCodes.includes(o.code))
		.map((o) => o.label)
		.join(', ')
}

/**
 * How many options an axis needs before the list gets a filter box.
 *
 * A short list is faster to scan than to type into, and an input above three
 * checkboxes is clutter. Geography (23 values) and brand (196) are the axes this
 * exists for.
 */
const FILTER_THRESHOLD = 8

/**
 * The open list: the filter box and the value rows.
 *
 * Its own component because React Aria unmounts a closed popover's subtree, so holding `query`
 * HERE means closing the list forgets the query. State on the parent would outlive the popover
 * and reopen showing two of 196 brands with nothing on screen explaining why.
 */
function AxisOptionList({
	axis,
	selectedCodes,
	onToggle,
}: Pick<AxisMultiSelectProps, 'axis' | 'selectedCodes' | 'onToggle'>) {
	const [query, setQuery] = useState('')
	const showFilter = axis.options.length >= FILTER_THRESHOLD

	const visible = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (needle === '') return axis.options
		return axis.options.filter((o) => `${o.label} ${o.code}`.toLowerCase().includes(needle))
	}, [axis.options, query])

	return (
		<>
			{showFilter && (
				<div className="relative mb-1">
					<Search
						className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ds-text-subtle"
						aria-hidden="true"
					/>
					{/* `autoFocus` so opening the list and typing is one gesture: the popover is
					    already an explicit choice to interact with this axis. */}
					<input
						// biome-ignore lint/a11y/noAutofocus: focus follows the popover the user just opened
						autoFocus
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={`Filter ${axis.label.toLowerCase()}…`}
						aria-label={`Filter ${axis.label} values`}
						className="w-full rounded-ds-sm border border-ds-border bg-ds-surface py-1.5 pl-7 pr-2 text-ds-sm leading-5 text-ds-text placeholder:text-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
					/>
				</div>
			)}
			<div className="max-h-60 overflow-auto" role="listbox" aria-label={axis.label} aria-multiselectable="true">
				{axis.options.length === 0 &&
					(axis.isLoading ? (
						<div className="px-2 py-1.5">
							<Skeleton shape="text" className="h-4 w-24" />
							<span className="sr-only">Loading values…</span>
						</div>
					) : (
						<EmptyState className="border-0 p-2 text-left" title="No values" />
					))}
				{/* Distinct from "No values": the axis HAS values, this query just matches none of
				    them, and the fix is to change the query rather than the selection. */}
				{axis.options.length > 0 && visible.length === 0 && (
					<EmptyState
						className="border-0 p-2 text-left"
						variant="filtered"
						title={`No values match “${query.trim()}”`}
					/>
				)}
				{visible.map((opt) => {
					const selected = selectedCodes.includes(opt.code)
					return (
						<button
							key={opt.code}
							type="button"
							role="option"
							aria-selected={selected}
							onClick={() => onToggle(opt.code)}
							className="flex w-full items-center gap-2 rounded-ds-sm px-2 py-1.5 text-left text-ds-sm leading-5 text-ds-text-muted hover:bg-ds-surface"
							style={{ paddingLeft: `${0.5 + opt.depth}rem` }}
						>
							<span
								className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-ds-sm border ${
									selected
										? 'border-ds-primary bg-ds-primary text-ds-background'
										: 'border-ds-border bg-ds-surface-elevated'
								}`}
							>
								{selected && <Check className="h-3 w-3" aria-hidden="true" />}
							</span>
							<span className="truncate">{opt.label}</span>
						</button>
					)
				})}
			</div>
		</>
	)
}

/**
 * A single axis rendered as a multi-select dropdown: a trigger showing the
 * current selection summary, and a popover with a checkable row per value.
 * Hierarchical values are indented by `depth`. Presentational and prop-driven.
 *
 * Long lists get a filter box, matching on LABEL and CODE: a reader searching
 * "bravecto" and one searching the code they saw in an export both find the row.
 * A selected value that the query hides stays selected — filtering narrows what
 * is visible, never what is chosen.
 */
export function AxisMultiSelect({ axis, selectedCodes, onToggle, onRemove }: AxisMultiSelectProps) {
	const count = selectedCodes.length

	return (
		<div className="space-y-2">
			<span className="flex items-center justify-between gap-2">
				<h3 className="text-ds-sm leading-5 font-semibold text-ds-text">{axis.label}</h3>
				{onRemove && (
					<button
						type="button"
						onClick={onRemove}
						aria-label={`Remove ${axis.label} filter`}
						className="rounded-ds-sm p-0.5 text-ds-text-subtle hover:bg-ds-surface hover:text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border"
					>
						<X className="h-3.5 w-3.5" aria-hidden="true" />
					</button>
				)}
			</span>
			<Popover>
				<PopoverTrigger
					className={cn(
						TRIGGER_CLASS,
						'hover:border-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border',
					)}
				>
					<span className={count > 0 ? 'truncate text-ds-text' : 'truncate text-ds-text-subtle'}>
						{summary(axis, selectedCodes)}
					</span>
					<span className="flex shrink-0 items-center gap-1.5">
						{count > 0 && (
							<Badge size="sm" className="px-1.5">
								{count}
							</Badge>
						)}
						<ChevronDown className="h-4 w-4 text-ds-text-subtle" aria-hidden="true" />
					</span>
				</PopoverTrigger>
				<PopoverContent className="w-(--trigger-width) p-1">
					<AxisOptionList axis={axis} selectedCodes={selectedCodes} onToggle={onToggle} />
				</PopoverContent>
			</Popover>
		</div>
	)
}

/**
 * An axis group whose axis is not known yet — the registry read itself is still in flight,
 * so there is no label to show and no values to offer.
 *
 * Distinct from an axis with `isLoading`: that one has a name and a live control, and says
 * "Loading…" inside it. This one stands in for a group that has nothing at all yet, at the
 * real label and trigger heights so the rail keeps its box and the controls land where the
 * placeholders stood.
 */
export function AxisMultiSelectSkeleton() {
	return (
		<div className="space-y-2">
			<Skeleton shape="text" className="h-5 w-24" />
			<div className={TRIGGER_CLASS}>
				<Skeleton shape="text" className="h-3 w-16" />
				<ChevronDown className="h-4 w-4 text-ds-text-subtle" aria-hidden="true" />
			</div>
		</div>
	)
}
